import type {
  MutableVec2,
  RunwayDefinition,
  Vec2,
} from "../airport/AirportDefinition";
import { axisProgress, clonePoint, distance } from "../simulation/geometry";
import {
  AIRCRAFT_SPECS,
  type AircraftKind,
  type AircraftSpec,
} from "./AircraftCatalog";
import type { AircraftState } from "./AircraftState";
import { type Airframe, airframeFor } from "./Airframes";

/**
 * Live aircraft record.
 *
 * Deliberately mutable: the simulation advances hundreds of these per second
 * and the renderer diffs them by id.
 */
export interface Aircraft {
  readonly id: number;
  readonly kind: AircraftKind;
  /** Colour spec: nominal speed and size, separation radius, runway rule. */
  readonly spec: AircraftSpec;
  /**
   * Body inside that colour. Scales the nominal speed and length, so read
   * `cruiseSpeed`/`bodyLength` rather than `spec.speed`/`spec.length`.
   */
  readonly airframe: Airframe;
  pos: MutableVec2;
  heading: number;
  path: MutableVec2[];
  state: AircraftState;
  /** Runway this aircraft is committed to, once a landing is accepted. */
  target: RunwayDefinition | null;
  /** 0..1 along the runway axis while landing. */
  landProgress: number;
  /** 0..1 along the runway axis while departing. */
  takeoffProgress: number;
  departureReadyAt: number | null;
  scheduledDepartureAt: number | null;
  departureQueueOrder: number | null;
  age: number;
  /**
   * Whether this aircraft has ever been inside the view.
   *
   * Arrivals are born outside it, and on a long, obliquely entered view can
   * take over ten seconds to fly in. Until they manage it they are inbound
   * traffic, not litter, so the cull sweep leaves them alone.
   */
  enteredView: boolean;
}

/** Frozen view handed to the UI. */
export interface AircraftSnapshot {
  readonly id: number;
  readonly kind: AircraftKind;
  readonly state: AircraftState;
  readonly position: Vec2;
  readonly headingRadians: number;
  readonly path: readonly Vec2[];
  readonly targetRunwayId: string | null;
}

let nextAircraftId = 1;

/** Test hook so ids stay comparable between runs. */
export function resetAircraftIds(): void {
  nextAircraftId = 1;
}

/**
 * `airframe` is passed in by the spawn director, which had to pick one early
 * to predict this arrival's speed; left out, it is derived from the new id.
 */
export function createAircraft(
  kind: AircraftKind,
  position: Vec2,
  heading: number,
  airframe?: Airframe,
): Aircraft {
  const id = nextAircraftId++;
  return {
    id,
    kind,
    spec: AIRCRAFT_SPECS[kind],
    airframe: airframe ?? airframeFor(kind, id),
    pos: clonePoint(position),
    heading,
    path: [],
    state: "flying",
    target: null,
    landProgress: 0,
    takeoffProgress: 0,
    departureReadyAt: null,
    scheduledDepartureAt: null,
    departureQueueOrder: null,
    age: 0,
    enteredView: false,
  };
}

/** Cruise speed in world units per second: colour nominal × airframe. */
export function cruiseSpeed(aircraft: Aircraft): number {
  return aircraft.spec.speed * aircraft.airframe.speedFactor;
}

/** Body length in world units: colour nominal × airframe. */
export function bodyLength(aircraft: Aircraft): number {
  return aircraft.spec.length * aircraft.airframe.lengthFactor;
}

/** How far along the runway a landing aircraft has rolled. */
export function landingProgress(aircraft: Aircraft): number {
  if (!aircraft.target) {
    return 0;
  }
  const runwayLength = distance(aircraft.target.approach, aircraft.target.end);
  if (runwayLength < 1e-6) {
    return 1;
  }
  return Math.min(
    1,
    distance(aircraft.target.approach, aircraft.pos) / runwayLength,
  );
}

export function takeoffProgress(aircraft: Aircraft): number {
  if (!aircraft.target) {
    return 0;
  }
  return axisProgress(
    aircraft.pos,
    aircraft.target.approach,
    aircraft.target.end,
  );
}

/**
 * Airborne traffic: everything that can crash into something else.
 * A departure only counts once it has rolled far enough down the runway.
 */
export function isAirborne(
  aircraft: Aircraft,
  departureAirborneProgress: number,
): boolean {
  return (
    aircraft.state === "flying" ||
    (aircraft.state === "departing" &&
      aircraft.takeoffProgress >= departureAirborneProgress)
  );
}

export function aircraftSnapshot(aircraft: Aircraft): AircraftSnapshot {
  return Object.freeze({
    id: aircraft.id,
    kind: aircraft.kind,
    state: aircraft.state,
    position: Object.freeze(clonePoint(aircraft.pos)),
    headingRadians: aircraft.heading,
    path: Object.freeze(
      aircraft.path.map((point) => Object.freeze(clonePoint(point))),
    ),
    targetRunwayId: aircraft.target?.id ?? null,
  });
}
